// src/pages/admin/WorkflowManager.tsx - MODERN & INFORMATIVE
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit, Trash2, ChevronDown, ChevronUp,
  AlertTriangle, ArrowRight, Shield, GitBranch, Layers,
  CheckCircle, XCircle, Info, Zap, Building2,
  RefreshCw, AlertCircle, HelpCircle, ServerCog, ListTree
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Card, Breadcrumb, Modal, Input, Textarea, Select, Loading, LogoLoadingMark, LoadingLabel } from '../../components/ui';
import { workflowService } from '../../services/workflow';
import { useCapabilities } from '../../hooks/useCapabilities';
import toast from 'react-hot-toast';

// ========== TYPES ==========

interface StepData {
  id?: number;
  step_order: number;
  validator_strategy: string;
  validator_params: Record<string, any> | null;
  step_label: string;
  can_skip: boolean;
  skip_condition: Record<string, any> | null;
  can_resolve_here: boolean;
}

interface ConfigData {
  id: number;
  source_unit_type: string;
  name: string;
  description: string | null;
  max_resolution_level: string;
  validation_step_count: number;
  is_active: boolean;
  priority: number;
  steps: StepData[];
}

interface StrategyOption {
  value: string;
  label: string;
  description: string;
}

interface ResolutionOption {
  value: string;
  label: string;
}

// ========== CONSTANTS ==========

const UNIT_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string; description: string; flowHint: string }> = {
  PRODI: {
    label: 'Program Studi',
    icon: '🎓',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    description: 'Laporan dari unit Program Studi (Kaprodi/Staff Prodi)',
    flowHint: 'Alur standar: Kaprodi submit → Dekan validasi (L1) → Warek Bidang validasi (L2) → RAPIM jika perlu',
  },
  FAKULTAS: {
    label: 'Fakultas',
    icon: '🏫',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Laporan dari unit Fakultas (Dekan/Staff Fakultas)',
    flowHint: 'Alur: Dekan submit → Warek Bidang validasi → RAPIM jika perlu',
  },
  BAGIAN: {
    label: 'Bagian',
    icon: '📁',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    description: 'Laporan dari unit Bagian (langsung di bawah universitas)',
    flowHint: 'Alur: Kabag submit → Warek Bidang validasi → RAPIM jika perlu. Tanpa validasi Dekan karena Bagian tidak di bawah Fakultas.',
  },
  LAB: {
    label: 'Laboratorium',
    icon: '🔬',
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    description: 'Laporan dari unit Laboratorium (di bawah Pusat/Prodi)',
    flowHint: 'Alur: Kalab submit → Ka Pusat/Unit Induk (L1) → Dekan (L2) → Warek Bidang (L3) → RAPIM jika perlu. Setiap level bisa menyelesaikan langsung.',
  },
  PUSAT: {
    label: 'Pusat/UPT',
    icon: '🏢',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    description: 'Laporan dari unit Pusat/UPT (di bawah Lembaga/Fakultas)',
    flowHint: 'Alur: Ka Pusat submit → Pimpinan Unit Induk validasi → RAPIM jika perlu. Parent bisa Ka Lembaga atau Dekan tergantung struktur.',
  },
  LEMBAGA: {
    label: 'Lembaga',
    icon: '📖',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    description: 'Laporan dari unit Lembaga (Ka Lembaga/Staff)',
    flowHint: 'Alur: Ka Lembaga submit → Warek Bidang validasi → RAPIM jika perlu',
  },
  TU: {
    label: 'Tata Usaha',
    icon: '📋',
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    description: 'Laporan dari unit Tata Usaha',
    flowHint: 'Belum ada workflow khusus — akan menggunakan workflow DEFAULT (Pimpinan Unit Induk → Warek Bidang)',
  },
  WAREK: {
    label: 'Wakil Rektor',
    icon: '🏛️',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    description: 'Laporan dari unit Wakil Rektor',
    flowHint: 'Belum ada workflow khusus — akan menggunakan workflow DEFAULT',
  },
  UNIVERSITAS: {
    label: 'Universitas',
    icon: '📌',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    description: 'Laporan dari unit Universitas',
    flowHint: 'Belum ada workflow khusus — akan menggunakan workflow DEFAULT',
  },
  DEFAULT: {
    label: 'Default (Fallback)',
    icon: '⚙️',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    description: 'Digunakan otomatis jika tipe unit pelapor tidak punya workflow khusus',
    flowHint: 'Alur fallback: Pimpinan Unit Induk (L1) → Warek Bidang (L2) → RAPIM jika perlu',
  },
};

const STRATEGY_ICONS: Record<string, { icon: string; color: string }> = {
  hierarchy_parent: { icon: '⬆️', color: 'bg-blue-100 text-blue-700' },
  hierarchy_grandparent: { icon: '⬆️⬆️', color: 'bg-indigo-100 text-indigo-700' },
  role_scope_match: { icon: '🎯', color: 'bg-purple-100 text-purple-700' },
  specific_role: { icon: '👤', color: 'bg-amber-100 text-amber-700' },
  unit_leader: { icon: '👑', color: 'bg-teal-100 text-teal-700' },
};

const RESOLUTION_ICONS: Record<string, { icon: string; color: string }> = {
  fakultas: { icon: '🏫', color: 'text-blue-600' },
  lembaga: { icon: '📖', color: 'text-emerald-600' },
  warek: { icon: '🏛️', color: 'text-purple-600' },
  universitas: { icon: '📌', color: 'text-teal-600' },
};

// ========== SORTABLE STEP ROW ==========

const SortableStepRow = ({ step, configId, openEditStep, handleDeleteStep, strategyLabel, sIcon }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id || `temp-${step.step_order}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 bg-white rounded-xl border ${isDragging ? 'border-purple-400 shadow-lg' : 'border-neutral-border/50'} hover:border-purple-200 hover:shadow-sm transition-all group`}
    >
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab hover:bg-neutral-bg p-2 rounded-lg text-neutral-text-secondary active:cursor-grabbing shrink-0"
      >
        <ListTree className="w-5 h-5 text-gray-400" />
      </div>

      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white flex items-center justify-center text-sm font-black shadow-md shrink-0">
        {step.step_order}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-neutral-text-primary">{step.step_label}</p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-lg ${sIcon.color}`}>
            {sIcon.icon} {strategyLabel}
          </span>
          {step.can_resolve_here && (
            <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-lg">
              ✓ Bisa Selesaikan
            </span>
          )}
          {step.can_skip && (
            <span className="text-[10px] font-semibold px-2 py-0.5 bg-amber-100 text-amber-700 rounded-lg">
              ⤵️ Bisa Di-skip
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => openEditStep(configId, step)} className="p-1.5 rounded-lg hover:bg-blue-50 text-neutral-text-secondary hover:text-blue-600 transition-colors">
          <Edit className="w-4 h-4" />
        </button>
        <button onClick={() => step.id && handleDeleteStep(configId, step.id)} className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ========== COMPONENT ==========

const WorkflowManager: React.FC = () => {
  const [configs, setConfigs] = useState<ConfigData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [strategies, setStrategies] = useState<StrategyOption[]>([]);
  const [resolutionLevels, setResolutionLevels] = useState<ResolutionOption[]>([]);

  // Modal states
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ConfigData | null>(null);
  const [configForm, setConfigForm] = useState({
    source_unit_type: '',
    name: '',
    description: '',
    max_resolution_level: 'universitas',
    priority: 0,
    is_active: true,
  });

  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState<StepData | null>(null);
  const [stepConfigId, setStepConfigId] = useState<number | null>(null);
  const [stepForm, setStepForm] = useState({
    step_order: 1,
    validator_strategy: 'hierarchy_parent',
    step_label: '',
    can_skip: false,
    can_resolve_here: true,
    skip_role: '',
    role_names: '',
    scope_to_ancestors: false,
    unit_id: '',
  });

  const [showGuide, setShowGuide] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulationConfig, setSimulationConfig] = useState<ConfigData | null>(null);
  
  const [dndPending, setDndPending] = useState<{
    configId: number;
    steps: StepData[];
    activeId: any;
    overId: any;
    isActiveConfig: boolean;
  } | null>(null);

  const [processing, setProcessing] = useState(false);
  const caps = useCapabilities();

  // DND Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const persistReorder = async (
    configId: number,
    steps: StepData[],
    activeId: any,
    overId: any
  ) => {
    const oldIndex = steps.findIndex((s) => (s.id || `temp-${s.step_order}`) === activeId);
    const newIndex = steps.findIndex((s) => (s.id || `temp-${s.step_order}`) === overId);

    const newSteps = arrayMove(steps, oldIndex, newIndex).map((s, i) => ({
      ...s,
      step_order: i + 1,
    }));

    setConfigs((prev) =>
      prev.map((c) => (c.id === configId ? { ...c, steps: newSteps } : c))
    );

    try {
      const payload = newSteps
        .filter((s) => s.id)
        .map((s) => ({ id: s.id!, step_order: s.step_order }));

      await workflowService.reorderSteps(configId, payload);
      toast.success('Urutan step berhasil disimpan');
    } catch (err) {
      toast.error('Gagal menyimpan urutan step');
      fetchData();
    }
  };

  const handleDragEnd = async (event: DragEndEvent, configId: number, steps: StepData[]) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const config = configs.find(c => c.id === configId);

    if (config?.is_active) {
      setDndPending({
        configId,
        steps,
        activeId: active.id,
        overId: over.id,
        isActiveConfig: true
      });
      return;
    }

    await persistReorder(configId, steps, active.id, over.id);
  };

  const confirmDragReorder = async () => {
    if (!dndPending) return;
    await persistReorder(
      dndPending.configId,
      dndPending.steps,
      dndPending.activeId,
      dndPending.overId
    );
    setDndPending(null);
  };

  // ========== FETCH ==========

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsData, strategiesData] = await Promise.all([
        workflowService.getConfigs(),
        workflowService.getStrategies(),
      ]);
      setConfigs(Array.isArray(configsData) ? configsData : []);
      setStrategies(strategiesData?.strategies || []);
      setResolutionLevels(strategiesData?.resolution_levels || []);
    } catch (err) {
      toast.error('Gagal memuat konfigurasi workflow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ========== CONFIG CRUD ==========

  const openCreateConfig = () => {
    setEditingConfig(null);
    setConfigForm({
      source_unit_type: '',
      name: '',
      description: '',
      max_resolution_level: 'universitas',
      priority: 0,
      is_active: true,
    });
    setShowConfigModal(true);
  };

  const openEditConfig = (config: ConfigData) => {
    setEditingConfig(config);
    setConfigForm({
      source_unit_type: config.source_unit_type,
      name: config.name,
      description: config.description || '',
      max_resolution_level: config.max_resolution_level,
      priority: config.priority,
      is_active: config.is_active,
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!configForm.source_unit_type.trim() || !configForm.name.trim()) {
      toast.error('Unit Type dan Nama wajib diisi');
      return;
    }

    setProcessing(true);
    try {
      if (editingConfig) {
        await workflowService.updateConfig(editingConfig.id, {
          name: configForm.name,
          description: configForm.description || null,
          max_resolution_level: configForm.max_resolution_level,
          priority: configForm.priority,
          is_active: configForm.is_active,
        } as any);
        toast.success('Workflow berhasil diperbarui');
      } else {
        await workflowService.createConfig({
          source_unit_type: configForm.source_unit_type.toUpperCase(),
          name: configForm.name,
          description: configForm.description || undefined,
          max_resolution_level: configForm.max_resolution_level,
          is_active: configForm.is_active,
          priority: configForm.priority,
          steps: [{
            step_order: 1,
            validator_strategy: 'hierarchy_parent',
            step_label: 'Validasi Pimpinan',
            can_resolve_here: true,
          }],
        });
        toast.success('Workflow berhasil dibuat dengan 1 step default');
      }
      setShowConfigModal(false);
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteConfig = async (config: ConfigData) => {
    if (config.is_active) {
      const confirmed = window.confirm(
        `⚠️ Workflow "${config.name}" sedang AKTIF!\n\n` +
        `Menghapus workflow aktif bisa menyebabkan laporan yang sedang divalidasi mengalami error.\n\n` +
        `Disarankan: Nonaktifkan dulu, baru hapus.\n\nYakin tetap hapus?`
      );
      if (!confirmed) return;
    } else {
      if (!window.confirm(`Hapus workflow "${config.name}"?`)) return;
    }

    try {
      await workflowService.deleteConfig(config.id);
      toast.success('Workflow berhasil dihapus');
      fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Gagal menghapus';
      if (msg.includes('foreign key') || msg.includes('in use')) {
        toast.error('Workflow tidak bisa dihapus karena masih digunakan oleh laporan aktif');
      } else {
        toast.error(msg);
      }
    }
  };

  const handleToggleActive = async (config: ConfigData) => {
    try {
      await workflowService.toggleActive(config.id);
      toast.success(config.is_active ? 'Workflow dinonaktifkan' : 'Workflow diaktifkan');
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal mengubah status');
    }
  };

  // ========== TEMPLATES ==========
  
  const applyTemplate = async (templateType: 'prodi' | 'univ') => {
    if (!window.confirm('Buat workflow menggunakan template ini?')) return;
    setProcessing(true);
    try {
      if (templateType === 'prodi') {
        await workflowService.createConfig({
          source_unit_type: 'PRODI',
          name: 'Template Workflow Prodi Standard',
          description: 'Pelaporan masuk ke Dekan lalu ke Warek Bidang',
          max_resolution_level: 'universitas',
          is_active: false,
          priority: 5,
          steps: [
            { step_order: 1, validator_strategy: 'hierarchy_parent', step_label: 'Validasi Dekan', can_resolve_here: true },
            { step_order: 2, validator_strategy: 'role_scope_match', step_label: 'Validasi Warek Bidang', can_resolve_here: true }
          ],
        });
      } else {
        await workflowService.createConfig({
          source_unit_type: 'TU',
          name: 'Template Workflow Tata Usaha',
          description: 'Pelaporan yang langsung mengarah ke Warek 2',
          max_resolution_level: 'universitas',
          is_active: false,
          priority: 5,
          steps: [
            {
              step_order: 1,
              validator_strategy: 'specific_role',
              validator_params: {
                role_names: ['wakil_rektor_2'],
              },
              step_label: 'Validasi Warek 2',
              can_resolve_here: true
            }
          ],
        });
      }
      toast.success('Template berhasil diterapkan (Draft)!');
      fetchData();
    } catch (err) {
      toast.error('Gagal menyiapkan template');
    } finally {
      setProcessing(false);
    }
  };

  // ========== STEP CRUD ==========

  const openAddStep = (configId: number, currentStepCount: number) => {
    setStepConfigId(configId);
    setEditingStep(null);
    setStepForm({
      step_order: currentStepCount + 1,
      validator_strategy: 'hierarchy_parent',
      step_label: '',
      can_skip: false,
      can_resolve_here: true,
      skip_role: '',
      role_names: '',
      scope_to_ancestors: false,
      unit_id: '',
    });
    setShowStepModal(true);
  };

  const openEditStep = (configId: number, step: StepData) => {
    setStepConfigId(configId);
    setEditingStep(step);
    const skipRoles = step.skip_condition?.when_reporter_role || [];
    const params = step.validator_params || {};
    setStepForm({
      step_order: step.step_order,
      validator_strategy: step.validator_strategy,
      step_label: step.step_label,
      can_skip: step.can_skip,
      can_resolve_here: step.can_resolve_here,
      skip_role: Array.isArray(skipRoles) ? skipRoles.join(', ') : '',
      role_names: Array.isArray(params.role_names)
        ? params.role_names.join(', ')
        : '',
      scope_to_ancestors: !!params.scope_to_unit_ancestors,
      unit_id: params.unit_id ? String(params.unit_id) : '',
    });
    setShowStepModal(true);
  };

  const handleSaveStep = async () => {
    if (!stepConfigId || !stepForm.step_label.trim()) {
      toast.error('Label step wajib diisi');
      return;
    }

    if (stepForm.validator_strategy === 'specific_role') {
      const names = stepForm.role_names
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (names.length === 0) {
        toast.error('Minimal satu role name harus diisi untuk strategy "Role Tertentu"');
        return;
      }
    }

    if (stepForm.validator_strategy === 'unit_leader' && stepForm.unit_id) {
      const parsed = parseInt(stepForm.unit_id, 10);
      if (isNaN(parsed) || parsed <= 0) {
        toast.error('Unit ID harus berupa angka positif');
        return;
      }
    }

    const skipCondition = stepForm.can_skip && stepForm.skip_role.trim()
      ? { when_reporter_role: stepForm.skip_role.split(',').map(s => s.trim()).filter(Boolean) }
      : null;

    let validatorParams: Record<string, any> | null = null;

    switch (stepForm.validator_strategy) {
      case 'role_scope_match':
        validatorParams = { match_by: 'kategori_masalah' };
        break;
      case 'specific_role': {
        const roleNames = stepForm.role_names
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        validatorParams = { role_names: roleNames };
        if (stepForm.scope_to_ancestors) {
          validatorParams.scope_to_unit_ancestors = true;
        }
        break;
      }
      case 'unit_leader':
        if (stepForm.unit_id) {
          validatorParams = { unit_id: parseInt(stepForm.unit_id, 10) };
        }
        break;
      default:
        validatorParams = null;
        break;
    }

    const payload = {
      step_order: stepForm.step_order,
      validator_strategy: stepForm.validator_strategy,
      validator_params: validatorParams,
      step_label: stepForm.step_label,
      can_skip: stepForm.can_skip,
      skip_condition: skipCondition,
      can_resolve_here: stepForm.can_resolve_here,
    };

    setProcessing(true);
    try {
      if (editingStep?.id) {
        await workflowService.updateStep(stepConfigId, editingStep.id, payload);
        toast.success('Step berhasil diperbarui');
      } else {
        await workflowService.addStep(stepConfigId, payload);
        toast.success('Step berhasil ditambahkan');
      }
      setShowStepModal(false);
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal menyimpan step');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteStep = async (configId: number, stepId: number) => {
    const config = configs.find(c => c.id === configId);

    if (config?.is_active) {
      if (!window.confirm('⚠️ Workflow ini sedang AKTIF!\nMenghapus step bisa mempengaruhi laporan yang sedang divalidasi.\n\nYakin?')) return;
    } else {
      if (!window.confirm('Hapus step ini?')) return;
    }

    const currentSteps = config?.steps || [];
    if (currentSteps.length <= 1) {
      toast.error('Workflow harus memiliki minimal 1 step');
      return;
    }

    try {
      await workflowService.deleteStep(configId, stepId);
      toast.success('Step berhasil dihapus');
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal menghapus step');
    }
  };

  // ========== HELPERS ==========

  const getUnitTypeConfig = (type: string) => UNIT_TYPE_CONFIG[type] || UNIT_TYPE_CONFIG['DEFAULT'];
  const getStrategyIcon = (strategy: string) => STRATEGY_ICONS[strategy] || { icon: '⚙️', color: 'bg-gray-100 text-gray-700' };
  const getResolutionIcon = (level: string) => RESOLUTION_ICONS[level] || { icon: '📌', color: 'text-gray-600' };

  const strategyLabel = (val: string) => strategies.find(s => s.value === val)?.label || val;
  const resolutionLabel = (val: string) => resolutionLevels.find(r => r.value === val)?.label || val;

  // ========== GUARD ==========

  if (!caps.canManageSettings) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Workflow' }]} />
        <Card className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-warning" />
          <p className="text-lg font-bold">Akses Terbatas</p>
          <p className="text-sm text-neutral-text-secondary mt-1">Hanya Administrator yang dapat mengelola workflow.</p>
        </Card>
      </div>
    );
  }

  // ========== RENDER ==========

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Workflow Manager' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-purple-500" />
            Workflow Manager
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Konfigurasi alur validasi per tipe unit organisasi
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuide(!showGuide)}
          >
            Panduan
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={loading ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="primary" onClick={openCreateConfig} icon={<Plus className="w-4 h-4" />}>
            Buat Workflow
          </Button>
        </div>
      </div>

      {/* Guide Panel */}
      {showGuide && (
        <Card className="!bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 animate-in">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-sm font-bold text-blue-900">Panduan Workflow Engine</h3>
            </div>
            <button onClick={() => setShowGuide(false)} className="text-blue-400 hover:text-blue-600">
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cara Kerja */}
            <div>
              <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-3">Cara Kerja</p>
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Laporan Diajukan', desc: 'User dari unit tertentu (misal: Prodi) mengajukan laporan masalah' },
                  { step: '2', title: 'Sistem Cari Workflow', desc: 'Sistem mencari konfigurasi workflow yang sesuai dengan tipe unit pelapor' },
                  { step: '3', title: 'Jalankan Step Validasi', desc: 'Setiap step dijalankan berurutan — sistem mencari validator berdasarkan strategi yang dipilih' },
                  { step: '4', title: 'Validator Bertindak', desc: 'Validator bisa: Setujui (lanjut ke step berikutnya), Tolak (kembali ke pelapor), atau Selesaikan di level-nya' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-blue-900">{item.title}</p>
                      <p className="text-[11px] text-blue-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategi Validator */}
            <div>
              <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-3">Strategi Pencarian Validator</p>
              <div className="space-y-2">
                {[
                  { icon: '⬆️', name: 'Pimpinan Unit Induk', desc: 'Mencari leader dari unit parent. Contoh: Prodi → Leader Fakultas (Dekan)' },
                  { icon: '⬆️⬆️', name: 'Pimpinan 2 Level Atas', desc: 'Naik 2 level. Contoh: Prodi → Universitas (Rektor)' },
                  { icon: '🎯', name: 'Warek Sesuai Bidang', desc: 'Mencocokkan Warek berdasarkan Role Scopes & kategori masalah' },
                  { icon: '👤', name: 'Role Spesifik', desc: 'Mencari user dengan role tertentu yang sudah ditentukan' },
                  { icon: '👑', name: 'Pimpinan Unit', desc: 'Langsung ke leader dari unit pelapor' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-white/50 rounded-lg">
                    <span className="text-sm shrink-0">{item.icon}</span>
                    <div>
                      <p className="text-xs font-semibold text-blue-900">{item.name}</p>
                      <p className="text-[10px] text-blue-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contoh Alur */}
          <div className="mt-4 pt-4 border-t border-blue-200">
            <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-3">Contoh: Workflow Prodi (2 Step)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1.5 bg-gray-200 rounded-lg text-xs font-medium">📝 Kaprodi Submit</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span className="px-3 py-1.5 bg-blue-200 text-blue-800 rounded-lg text-xs font-medium">⬆️ Step 1: Dekan (Bisa Selesaikan ✓)</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span className="px-3 py-1.5 bg-purple-200 text-purple-800 rounded-lg text-xs font-medium">🎯 Step 2: Warek Bidang</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span className="px-3 py-1.5 bg-teal-200 text-teal-800 rounded-lg text-xs font-medium">📌 RAPIM / Selesai</span>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-purple-600">{configs.length}</p>
              <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">Total Workflow</p>
            </div>
          </div>
        </Card>
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-black text-success">{configs.filter(c => c.is_active).length}</p>
              <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">Aktif</p>
            </div>
          </div>
        </Card>
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Layers className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-blue-600">
                {configs.reduce((sum, c) => sum + (c.steps?.length || 0), 0)}
              </p>
              <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">Total Step</p>
            </div>
          </div>
        </Card>
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-amber-600">
                {new Set(configs.map(c => c.source_unit_type)).size}
              </p>
              <p className="text-[10px] text-neutral-text-secondary font-semibold uppercase">Unit Type</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Loading / Empty / List */}
      {loading ? (
        <Loading variant="inline" message="Memuat konfigurasi workflow..." className="py-8" />
      ) : configs.length === 0 ? (
        <Card className="text-center py-16">
          <GitBranch className="w-16 h-16 mx-auto mb-4 text-neutral-text-secondary opacity-20" />
          <p className="text-lg font-bold text-neutral-text-primary">Belum Ada Workflow</p>
          <p className="text-sm text-neutral-text-secondary mt-1 max-w-md mx-auto">
            Buat konfigurasi workflow untuk mengatur alur validasi laporan secara otomatis.
            Setiap tipe unit bisa memiliki workflow berbeda.
          </p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button variant="primary" onClick={openCreateConfig} icon={<Plus className="w-4 h-4" />}>
              Buat Workflow Kosong
            </Button>
            <Button variant="outline" onClick={() => applyTemplate('prodi')} icon={<ServerCog className="w-4 h-4" />}>
              Gunakan Template Prodi
            </Button>
            <Button variant="outline" onClick={() => setShowGuide(true)} icon={<HelpCircle className="w-4 h-4" />}>
              Lihat Panduan
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {configs.map((config) => {
            const isExpanded = expandedId === config.id;
            const steps = (config.steps || []).sort((a, b) => a.step_order - b.step_order);
            const unitConfig = getUnitTypeConfig(config.source_unit_type);

            return (
              <Card
                key={config.id}
                className={`transition-all overflow-hidden ${!config.is_active ? 'opacity-60' : ''
                  } ${isExpanded ? 'ring-2 ring-purple-200 shadow-lg' : ''}`}
              >
                {/* Config Header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    {/* Unit Type Badge */}
                    <div className="text-3xl shrink-0">{unitConfig.icon}</div>

                    <div className="flex-1 min-w-0">
                      {/* Tags Row */}
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`px-2.5 py-0.5 rounded-lg text-xs font-bold border ${unitConfig.color}`}>
                          {config.source_unit_type}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${config.is_active
                          ? 'bg-success/10 text-success border border-success/20'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                          }`}>
                          {config.is_active ? '● Aktif' : '○ Nonaktif'}
                        </span>
                        <span className="text-[10px] text-neutral-text-secondary bg-neutral-bg px-2 py-0.5 rounded">
                          Prioritas: {config.priority}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-base font-bold text-neutral-text-primary">{config.name}</h3>

                      {config.description && (
                        <p className="text-xs text-neutral-text-secondary mt-1 line-clamp-2">{config.description}</p>
                      )}

                      {/* Meta Info */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                        <span className="flex items-center gap-1">
                          <Layers className="w-3.5 h-3.5" />
                          {steps.length} step
                        </span>
                        <span className="flex items-center gap-1">
                          {getResolutionIcon(config.max_resolution_level).icon}
                          Maks: {resolutionLabel(config.max_resolution_level)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {unitConfig.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggleActive(config)}
                      className={`p-2 rounded-lg transition-colors ${config.is_active
                        ? 'hover:bg-success/10 text-success'
                        : 'hover:bg-neutral-bg text-neutral-text-secondary'
                        }`}
                      title={config.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                    >
                      {config.is_active
                        ? <CheckCircle className="w-5 h-5" />
                        : <XCircle className="w-5 h-5" />
                      }
                    </button>
                    <button onClick={() => openEditConfig(config)} className="p-2 rounded-lg hover:bg-blue-50 text-neutral-text-secondary hover:text-blue-600 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteConfig(config)} className="p-2 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => setExpandedId(isExpanded ? null : config.id)} className="p-2 rounded-lg hover:bg-neutral-bg transition-colors">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Visual Flow - Always Visible */}
                <div className="mt-4 p-3 bg-neutral-bg/50 rounded-xl">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="px-2.5 py-1 bg-gray-200 rounded-lg text-xs font-medium">📝 Pelapor</span>
                    {steps.map((step, i) => {
                      const sIcon = getStrategyIcon(step.validator_strategy);
                      return (
                        <React.Fragment key={i}>
                          <ArrowRight className="w-4 h-4 text-neutral-border shrink-0" />
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${step.can_resolve_here
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-blue-100 text-blue-700 border-blue-200'
                            }`}>
                            {sIcon.icon} {step.step_label}
                            {step.can_resolve_here && ' ✓'}
                            {step.can_skip && ' ⤵️'}
                          </span>
                        </React.Fragment>
                      );
                    })}
                    <ArrowRight className="w-4 h-4 text-neutral-border shrink-0" />
                    <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium border border-purple-200">
                      {getResolutionIcon(config.max_resolution_level).icon} {resolutionLabel(config.max_resolution_level)}
                    </span>
                  </div>
                </div>

                {/* Expanded: Steps Detail */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-neutral-border space-y-4 animate-in">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Step Validasi ({steps.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setSimulationConfig(config); setShowSimulationModal(true); }} 
                          icon={<Zap className="w-3.5 h-3.5 text-yellow-500" />}
                        >
                          Simulasi Alur
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openAddStep(config.id, steps.length)} 
                          icon={<Plus className="w-3.5 h-3.5" />}
                        >
                          Tambah Step
                        </Button>
                      </div>
                    </div>

                    {steps.length === 0 ? (
                      <div className="text-center py-6 bg-neutral-bg/50 rounded-xl">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                        <p className="text-sm text-neutral-text-secondary">Belum ada step. Tambahkan minimal 1 step.</p>
                      </div>
                    ) : (
                      <DndContext 
                        sensors={sensors} 
                        collisionDetection={closestCenter} 
                        onDragEnd={(e) => handleDragEnd(e, config.id, steps)}
                      >
                        <SortableContext 
                          items={steps.map((s) => s.id || `temp-${s.step_order}`)} 
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-3">
                            {steps.map((step) => (
                              <SortableStepRow
                                key={step.id || `temp-${step.step_order}`}
                                step={step}
                                configId={config.id}
                                openEditStep={openEditStep}
                                handleDeleteStep={handleDeleteStep}
                                strategyLabel={strategyLabel(step.validator_strategy)}
                                sIcon={getStrategyIcon(step.validator_strategy)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ========== CONFIG MODAL ========== */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title={editingConfig ? '✏️ Edit Workflow' : '➕ Buat Workflow Baru'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSaveConfig} disabled={processing}>
              {processing ? <LoadingLabel label={editingConfig ? 'Menyimpan...' : 'Membuat workflow...'} /> : editingConfig ? 'Simpan' : 'Buat Workflow'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Info */}
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-xs text-blue-700">
              <Info className="w-3.5 h-3.5 inline mr-1" />
              {editingConfig
                ? 'Ubah konfigurasi workflow. Perubahan berlaku untuk laporan baru.'
                : 'Workflow baru akan dibuat dengan 1 step default (Validasi Pimpinan). Anda bisa menambah step setelah dibuat.'
              }
            </p>
          </div>

          {!editingConfig && (
            <div>
              <Select
                label="Tipe Unit Sumber"
                placeholder="Pilih tipe unit..."
                options={Object.entries(UNIT_TYPE_CONFIG)
                  .filter(([key]) => key !== 'DEFAULT')
                  .map(([key, val]) => ({
                    value: key,
                    label: `${val.icon} ${val.label}`,
                  }))}
                value={configForm.source_unit_type}
                onChange={(v) => setConfigForm({ ...configForm, source_unit_type: v })}
                required
              />
              {configForm.source_unit_type && (
                <div className="mt-1.5 space-y-1">
                  <p className="text-xs text-neutral-text-secondary">
                    ℹ️ {getUnitTypeConfig(configForm.source_unit_type).description}
                  </p>
                  <p className="text-[11px] text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg border border-blue-100">
                    💡 {getUnitTypeConfig(configForm.source_unit_type).flowHint}
                  </p>
                </div>
              )}
            </div>
          )}

          <Input
            label="Nama Workflow"
            placeholder="Contoh: Workflow Prodi (2 Step Validasi)"
            value={configForm.name}
            onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
            required
          />

          <Textarea
            label="Deskripsi (opsional)"
            placeholder="Jelaskan alur workflow ini..."
            value={configForm.description}
            onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
            rows={2}
          />

          <div>
            <Select
              label="Level Penyelesaian Maksimal"
              options={resolutionLevels.map(r => ({ value: r.value, label: r.label }))}
              value={configForm.max_resolution_level}
              onChange={(v) => setConfigForm({ ...configForm, max_resolution_level: v })}
            />
            <div className="mt-1.5 space-y-1">
              <p className="text-xs text-neutral-text-secondary">
                Menentukan batas tertinggi penyelesaian masalah sebelum masuk RAPIM.
              </p>
              {configForm.max_resolution_level === 'universitas' ? (
                <p className="text-[11px] text-teal-700 bg-teal-50 px-2.5 py-1.5 rounded-lg border border-teal-100">
                  📌 Level "Universitas" = Jika semua validator approve tanpa resolve, laporan akan <strong>masuk antrean RAPIM</strong> untuk dibahas di rapat pimpinan.
                </p>
              ) : (
                <p className="text-[11px] text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-100">
                  ⚠️ Level selain "Universitas" = Laporan <strong>TIDAK akan masuk RAPIM</strong>. Setelah semua step selesai, laporan langsung dianggap tervalidasi penuh.
                </p>
              )}
            </div>
          </div>

          <div>
            <Input
              label="Prioritas"
              type="number"
              value={configForm.priority.toString()}
              onChange={(e) => setConfigForm({ ...configForm, priority: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-neutral-text-secondary mt-1.5">
              Jika ada 2 workflow untuk unit type yang sama, prioritas tertinggi yang dipakai
            </p>
          </div>
        </div>
      </Modal>

      {/* ========== STEP MODAL ========== */}
      <Modal
        isOpen={showStepModal}
        onClose={() => setShowStepModal(false)}
        title={editingStep ? '✏️ Edit Step' : '➕ Tambah Step Validasi'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowStepModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSaveStep} disabled={processing}>
              {processing ? <LoadingLabel label={editingStep ? 'Menyimpan...' : 'Menambahkan step...'} /> : editingStep ? 'Simpan' : 'Tambah Step'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Label Step"
            placeholder="Contoh: Validasi Dekan, Validasi Warek Bidang"
            value={stepForm.step_label}
            onChange={(e) => setStepForm({ ...stepForm, step_label: e.target.value })}
            required
            helpText="Nama yang akan ditampilkan kepada validator"
          />

          <Input
            label="Urutan Step"
            type="number"
            min={1}
            value={stepForm.step_order.toString()}
            onChange={(e) => setStepForm({ ...stepForm, step_order: parseInt(e.target.value) || 1 })}
            helpText="Step dijalankan berurutan dari kecil ke besar"
          />

          {/* Strategy Selection */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
              Strategi Pencarian Validator
            </label>
            <div className="space-y-2">
              {strategies.map((s) => {
                const sIcon = getStrategyIcon(s.value);
                return (
                  <label
                    key={s.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${stepForm.validator_strategy === s.value
                      ? 'border-purple-400 bg-purple-50 shadow-sm'
                      : 'border-neutral-border hover:border-purple-200'
                      }`}
                  >
                    <input
                      type="radio"
                      name="strategy"
                      value={s.value}
                      checked={stepForm.validator_strategy === s.value}
                      onChange={() => setStepForm({ ...stepForm, validator_strategy: s.value })}
                      className="mt-0.5 text-purple-600 focus:ring-purple-300"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm px-1.5 py-0.5 rounded ${sIcon.color}`}>{sIcon.icon}</span>
                        <p className="text-sm font-semibold text-neutral-text-primary">{s.label}</p>
                      </div>
                      <p className="text-xs text-neutral-text-secondary mt-1">{s.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {stepForm.validator_strategy === 'specific_role' && (
            <div className="space-y-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">
                Parameter: Role Tertentu
              </p>
              <Input
                label="Role Names"
                placeholder="Contoh: wakil_rektor_2, dekan"
                value={stepForm.role_names}
                onChange={(e) => setStepForm({ ...stepForm, role_names: e.target.value })}
                required
                helpText="Pisahkan dengan koma. Gunakan nama role dari database, bukan display name."
              />
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={stepForm.scope_to_ancestors}
                  onChange={(e) => setStepForm({ ...stepForm, scope_to_ancestors: e.target.checked })}
                  className="rounded border-neutral-border text-teal-main focus:ring-teal-light mt-0.5"
                />
                <div>
                  <p className="text-sm font-semibold">Scope ke unit ancestor pelapor</p>
                  <p className="text-xs text-neutral-text-secondary">
                    Jika dicentang, pencarian validator dibatasi ke unit ancestor dari pelapor.
                  </p>
                </div>
              </label>
            </div>
          )}

          {stepForm.validator_strategy === 'unit_leader' && (
            <div className="space-y-3 p-4 bg-cyan-50 rounded-xl border border-cyan-200">
              <p className="text-xs font-bold text-cyan-800 uppercase tracking-wider mb-2">
                Parameter: Leader Unit
              </p>
              <Input
                label="Unit ID (opsional)"
                type="number"
                placeholder="Kosongkan untuk menggunakan unit pelapor"
                value={stepForm.unit_id}
                onChange={(e) => setStepForm({ ...stepForm, unit_id: e.target.value })}
                helpText="Jika diisi, validator dicari dari leader unit ini. Jika kosong, menggunakan unit pelapor."
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 p-4 bg-neutral-bg rounded-xl">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Opsi Step</p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={stepForm.can_resolve_here}
                onChange={(e) => setStepForm({ ...stepForm, can_resolve_here: e.target.checked })}
                className="rounded border-neutral-border text-teal-main focus:ring-teal-light mt-0.5"
              />
              <div>
                <p className="text-sm font-semibold">✓ Bisa diselesaikan di level ini</p>
                <p className="text-xs text-neutral-text-secondary">
                  Validator bisa memilih "Selesaikan di Level Saya" tanpa meneruskan ke step berikutnya.
                  Cocok untuk masalah yang bisa ditangani langsung oleh Dekan tanpa perlu ke Warek.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={stepForm.can_skip}
                onChange={(e) => setStepForm({ ...stepForm, can_skip: e.target.checked })}
                className="rounded border-neutral-border text-teal-main focus:ring-teal-light mt-0.5"
              />
              <div>
                <p className="text-sm font-semibold">⤵️ Step ini bisa di-skip</p>
                <p className="text-xs text-neutral-text-secondary">
                  Step otomatis dilewati jika kondisi terpenuhi. Contoh: jika pelapor adalah Dekan, skip validasi L1.
                </p>
              </div>
            </label>

            {stepForm.can_skip && (
              <div className="pl-7">
                <Input
                  label="Skip jika role pelapor"
                  placeholder="Contoh: dekan, kabag"
                  value={stepForm.skip_role}
                  onChange={(e) => setStepForm({ ...stepForm, skip_role: e.target.value })}
                  helpText="Pisahkan dengan koma. Step akan otomatis di-skip jika pelapor memiliki role ini."
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* ========== SIMULATION MODAL ========== */}
      <Modal
        isOpen={showSimulationModal}
        onClose={() => setShowSimulationModal(false)}
        title="⚡ Simulasi Alur Workflow"
        size="lg"
      >
        <div className="space-y-6">
          <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
            <h4 className="text-sm font-bold text-yellow-800 flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4" /> Uji Coba Konfigurasi
            </h4>
            <p className="text-xs text-yellow-700">
              Berikut adalah perkiraan pergerakan dokumen pelaporan melalui workflow <strong>{simulationConfig?.name}</strong>. Fitur ini membantu memverifikasi apakah alur sudah sesuai sebelum *publish*.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-inner">
            <h5 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Skenario Laporan Baru Dibuat</h5>
            
            <div className="flex flex-col gap-3 relative z-10">
              {/* Start Frame */}
              <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200 border-l-4 border-l-blue-400 shadow-sm relative">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 font-bold border border-blue-200">1</div>
                <div>
                  <p className="text-sm font-bold text-gray-900">User ({simulationConfig?.source_unit_type}) Submit Laporan</p>
                  <p className="text-xs text-gray-500">Memilih kategori \u0026 menyimpan laporan.</p>
                </div>
              </div>

              {simulationConfig?.steps.sort((a, b) => a.step_order - b.step_order).map((step, i) => (
                <div key={i} className="flex gap-4 relative">
                  <div className="flex flex-col items-center w-8 shrink-0 relative mt-1">
                    <div className="w-0.5 h-full bg-purple-200 absolute top-[-10px] bottom-0 left-1/2 -ml-[1px] -z-10"></div>
                  </div>
                  <div className="flex-1 mt-2 flex items-center gap-4 p-3 bg-white rounded-lg border border-purple-200 border-l-4 border-l-purple-500 shadow-sm relative">
                     <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold shrink-0 border border-purple-300">
                       {step.step_order + 1}
                     </div>
                     <div>
                       <p className="text-sm font-bold text-gray-900">{step.step_label}</p>
                       <p className="text-xs text-gray-500 mt-0.5"><span className="font-semibold text-purple-600">Strategi Pencarian:</span> {strategyLabel(step.validator_strategy)}</p>
                       {step.can_resolve_here && <p className="text-[10px] text-emerald-600 font-bold mt-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Validator ini diizinkan menyudahi alur</p>}
                     </div>
                  </div>
                </div>
              ))}

              {/* End Frame */}
              <div className="flex gap-4 relative">
                  <div className="flex flex-col items-center w-8 shrink-0 relative mt-1">
                    <div className="w-0.5 h-6 bg-gray-300 absolute top-[-10px] left-1/2 -ml-[1px] -z-10"></div>
                  </div>
                  <div className="flex-1 mt-2 flex items-center gap-4 p-3 bg-teal-50 rounded-lg border border-teal-200 border-l-4 border-l-teal-500 shadow-sm relative">
                     <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold shrink-0 border border-teal-300">
                       <Shield className="w-4 h-4" />
                     </div>
                     <div>
                       <p className="text-sm font-bold text-gray-900">Finalisasi / Penetapan</p>
                       <p className="text-xs text-gray-600 mt-0.5">Berakhir maksimal di level: <strong>{resolutionLabel(simulationConfig?.max_resolution_level || '')}</strong> (atau masuk Rekap RAPIM)</p>
                     </div>
                  </div>
              </div>

            </div>
          </div>
        </div>
      </Modal>

      {/* ========== DND WARNING MODAL ========== */}
      <Modal
        isOpen={!!dndPending}
        onClose={() => setDndPending(null)}
        title={dndPending?.isActiveConfig ? "⚠️ Peringatan Kritis" : "ℹ️ Mengubah Urutan Step"}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setDndPending(null)}>Batal</Button>
            <Button 
              variant={dndPending?.isActiveConfig ? "danger" : "primary"} 
              onClick={confirmDragReorder}
            >
              {dndPending?.isActiveConfig ? 'Ya, Tetap Pindahkan' : 'Ya, Pindahkan Urutan'}
            </Button>
          </div>
        }
      >
        <div className="space-y-6 pt-4">
          <div className="flex justify-center -mt-6 relative z-10 w-full mb-6">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center p-2 shadow-sm ${dndPending?.isActiveConfig ? 'bg-red-100 text-red-600 border-4 border-white' : 'bg-blue-100 text-blue-600 border-4 border-white'}`}>
              <AlertTriangle className="w-10 h-10" />
            </div>
          </div>
          
          <div className="text-center px-4">
            <h3 className="font-extrabold text-xl mb-2 text-neutral-text-primary">
              Anda yakin?
            </h3>
            
            {dndPending?.isActiveConfig ? (
              <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 mt-4 shadow-sm text-center animate-in fade-in slide-in-from-bottom-2">
                <p className="text-sm text-red-700 leading-relaxed font-semibold">
                  Workflow ini sedang <span className="underline">AKTIF</span>.
                </p>
                <div className="w-10 border-t border-red-300 mx-auto my-3"></div>
                <p className="text-xs text-red-600/90 tracking-wide rounded">
                  Mengubah urutan saat aktif sangant berisiko menyebabkan <strong>konflik validasi</strong> pada laporan yang saat ini sedang menunggu persetujuan. Pastikan Anda tahu risikonya.
                </p>
              </div>
            ) : (
              <p className="text-sm text-neutral-text-secondary leading-relaxed mt-2 bg-neutral-bg/50 p-4 rounded-xl border border-neutral-border">
                Perubahan posisi ini akan mengubah jalannya validasi laporan baru di masa depan secara langsung bergantung urutannya.
              </p>
            )}
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default WorkflowManager;
